import { useMutation, useQuery } from "@apollo/client";
import { SelectOption } from "@src/components/elements/Select/SelectOption";
import { matchNameToObject } from "@src/utils/matchNameToObject";
import { gql } from "@src/__generated__";
import { HrDepartmentFieldsFragment } from "@src/__generated__/graphql";
import { useEffect, useState } from "react";

gql(/* GraphQL */ `
  fragment HrDepartmentFields on HrDepartmentObject {
    id
    name
    departmentId
  }
`);

const GET_HR_DEPARTMENTS = gql(/* GraphQL */ `
  query GetHrDepartments {
    hrDepartments {
      ...HrDepartmentFields
    }
    departments {
      id
      name
    }
  }
`);

const UPDATE_HR_DEPARTMENT_MAPPINGS = gql(/* GraphQL */ `
  mutation UpdateHrDepartmentMappings($hrDepartments: [HrDepartmentInput!]!) {
    updateHrDepartments(hrDepartments: $hrDepartments)
  }
`);

export type HrDepartmentMapping = HrDepartmentFieldsFragment & {
  isInitiallyUnmapped: boolean;
  newDepartmentName: string | null;
};

export const useHrDepartmentsManager = () => {
  const { data, loading, refetch } = useQuery(GET_HR_DEPARTMENTS);
  const [hrDepartments, setHrDepartments] = useState<HrDepartmentMapping[]>([]);
  const [saveHrDepartments] = useMutation(UPDATE_HR_DEPARTMENT_MAPPINGS);

  useEffect(() => {
    if (data) {
      setHrDepartments(
        data.hrDepartments.map((hrDepartment) => {
          const departmentId = matchNameToObject(
            hrDepartment.name,
            data.departments,
          );
          const newDepartmentName = departmentId ? null : hrDepartment.name;
          return {
            ...hrDepartment,
            departmentId,
            isInitiallyUnmapped: !hrDepartment.departmentId,
            newDepartmentName,
          };
        }),
      );
    }
  }, [data]);

  function updateHrDepartment(
    id: string,
    newDepartmentId: string | null,
    newNewDepartmentName: string | null,
  ) {
    setHrDepartments(
      hrDepartments.map((hrDepartment) =>
        hrDepartment.id === id
          ? {
              ...hrDepartment,
              departmentId: newDepartmentId,
              newDepartmentName: newNewDepartmentName,
            }
          : hrDepartment,
      ),
    );
  }

  function onNewDepartmentName(newDepartmentName: string, id: string) {
    updateHrDepartment(id, null, newDepartmentName);
  }

  function onDepartmentChange(
    newDepartmentId: SelectOption | null,
    id: string,
  ) {
    if (newDepartmentId) {
      updateHrDepartment(
        id,
        newDepartmentId.value || null,
        newDepartmentId.value ? null : newDepartmentId.label,
      );
    }
  }

  const departmentOptions = (
    data?.departments.map(
      (department) =>
        ({
          value: department.id,
          label: department.name,
        } as SelectOption),
    ) ?? []
  ).concat(
    Array.from(
      new Set(
        hrDepartments
          .map((hrDepartment) => hrDepartment.newDepartmentName)
          .filter((n) => !!n),
      ),
    ).map((name) => ({ label: name ?? "", value: "" })),
  );

  return {
    hrDepartments,
    refetch,
    setHrDepartments,
    saveHrDepartments,
    updateHrDepartment,
    onNewDepartmentName,
    onDepartmentChange,
    departments: data?.departments ?? [],
    departmentOptions,
    loading,
  };
};
